import React, { Fragment, useState } from "react";
import { Toolbar } from "../../../components/Toolbar";
import { Collapse, Container, Row, Table } from "reactstrap";
import { AddSftpSettingModal } from "./AddSftpSettingModal";
import { AddSftpDirectoryModal } from "./AddSftpDirectoryModal";
import { EditSftpDirectoryModal } from "./EditSftpDirectoryModal";
import { DetailedAlert } from "../../../components/DetailedAlert";
import { AdministrateToggle } from "../../components/AdministrateToggle";
import { AdministrateResourceDeleteButton } from "../../components/AdministrateResourceDeleteButton";
import { camelizeKeys } from "../../../util/helpers";
import { ChevronDown, ChevronUp } from "lucide-react";
import { EditSftpSettingModal } from "./EditSftpSettingModal";
import { useUpdatePermission } from "../../utils";
import { extractErrorMessage } from "../../../util/api";

export const SftpSettings = ({ resource, refresh, resourcePath, showDocumentIngestion }) => {
  const [message, setMessage] = useState(null);
  const [canUpdate] = useUpdatePermission("SftpConnectionSetting");

  const setError = setMessage;

  const [open, setOpen] = useState(null);
  const accordion = (id) => {
    open === id ? setOpen() : setOpen(id);
  };

  const onAction = (results) => {
    setMessage(results.meta.message);
    refresh();
  };

  return (
    <>
      {canUpdate && (
        <Toolbar className="align-items-center mb-3">
          <Toolbar className="ms-auto">
            <AddSftpSettingModal
              onAdd={onAction}
              resourceId={resource.id}
              resourcePath={resourcePath}
              showDocumentIngestion={showDocumentIngestion}
            />
          </Toolbar>
        </Toolbar>
      )}

      {message && <DetailedAlert message={message} messageStyle="info" alertLinkClass="alert-link ms-2" />}

      <Table>
        <thead>
          <tr>
            <th>Host:Port</th>
            <th>Number of Sweeps</th>
            <th>Sweep Enabled</th>
            {showDocumentIngestion && <th>Document Ingestion</th>}
            {canUpdate && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {resource.sftpConnectionSettings.map((sftpSettings) => {
            const sftp = camelizeKeys(sftpSettings);
            return (
              <Fragment key={sftp.id}>
                <tr>
                  <td onClick={() => accordion(sftp.id)}>
                    {open === sftp.id ? <ChevronDown /> : <ChevronUp />}
                    {sftp.host}:{sftp.port}
                  </td>
                  <td>{sftp.sweeps}</td>
                  <td>
                    <AdministrateToggle
                      id={`sftp-toggle-${sftp.id}`}
                      toggle={sftp.enabled}
                      endpoint={`${resourcePath}/sftp_connection_settings/${sftp.id}`}
                      params={(toggle) => {
                        return { sftpSetting: { enabled: toggle } };
                      }}
                      onChange={setMessage}
                      onError={setMessage}
                      disabled={!canUpdate}
                    />
                  </td>
                  {showDocumentIngestion && (
                    <td>
                      <AdministrateToggle
                        id={`sftp-document-ingestion-toggle-${sftp.id}`}
                        toggle={sftp.isDocumentIngestion}
                        endpoint={`${resourcePath}/sftp_connection_settings/${sftp.id}`}
                        params={(toggle) => {
                          return { sftpSetting: { is_document_ingestion: toggle } };
                        }}
                        onChange={setMessage}
                        onError={setMessage}
                        disabled={!canUpdate}
                      />
                    </td>
                  )}
                  {canUpdate && (
                    <td>
                      <AddSftpDirectoryModal resourcePath={resourcePath} sftp={sftp} onAdd={onAction} />
                      <EditSftpSettingModal
                        resourceId={resource.id}
                        resourcePath={resourcePath}
                        sftp={sftp}
                        onEdit={onAction}
                        showDocumentIngestion={showDocumentIngestion}
                      />
                      <AdministrateResourceDeleteButton
                        deleteUrl={`${resourcePath}/sftp_connection_settings/${sftp.id}`}
                        onError={(err) => setError(extractErrorMessage(err))}
                        onSuccess={(results) => {
                          setMessage(`Successfully removed SFTP Setting ${sftp.host}`);
                          refresh();
                        }}
                      />
                    </td>
                  )}
                </tr>
                <tr>
                  <td colSpan="5">
                    <Collapse isOpen={open === sftp.id}>
                      <Table>
                        <thead>
                          <tr>
                            <th>Folder Path</th>
                            <th>Directory Enabled</th>
                            <th>Tilde Delimited File</th>
                            <th>Keep File in Directory</th>
                            {canUpdate && <th>Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {sftp.sftpDirectories.map((directorySnake) => {
                            const directory = camelizeKeys(directorySnake);
                            return (
                              <tr key={directory.id}>
                                <td>{directory.folderPath}</td>
                                <td>
                                  <AdministrateToggle
                                    id={`sftp-directory-toggle-${directory.id}`}
                                    toggle={directory.enabled}
                                    endpoint={`${resourcePath}/sftp_directories/${directory.id}`}
                                    params={(toggle) => {
                                      return { sftpDirectory: { id: directory.id, enabled: toggle } };
                                    }}
                                    onChange={setMessage}
                                    onError={setMessage}
                                    disabled={!canUpdate}
                                  />
                                </td>
                                <td>
                                  <AdministrateToggle
                                    id={`sftp-directory-tilde-toggle-${directory.id}`}
                                    toggle={directory.tildeDelimitedFile}
                                    endpoint={`${resourcePath}/sftp_directories/${directory.id}`}
                                    params={(toggle) => {
                                      return { sftpDirectory: { id: directory.id, tilde_delimited_file: toggle } };
                                    }}
                                    onChange={setMessage}
                                    onError={setMessage}
                                    disabled={!canUpdate}
                                  />
                                </td>
                                <td>
                                  <AdministrateToggle
                                    id={`sftp-directory-keep-file-toggle-${directory.id}`}
                                    toggle={directory.keepFileInDirectory}
                                    endpoint={`${resourcePath}/sftp_directories/${directory.id}`}
                                    params={(toggle) => {
                                      return { sftpDirectory: { id: directory.id, keep_file_in_directory: toggle } };
                                    }}
                                    onChange={setMessage}
                                    onError={setMessage}
                                    disabled={!canUpdate}
                                  />
                                </td>
                                {canUpdate && (
                                  <td>
                                    <EditSftpDirectoryModal
                                      onEdit={onAction}
                                      resourcePath={resourcePath}
                                      sftp={sftp}
                                      directory={directory}
                                    />
                                    <AdministrateResourceDeleteButton
                                      deleteUrl={`${resourcePath}/sftp_directories/${directory.id}`}
                                      onError={(err) => setError(extractErrorMessage(err))}
                                      onSuccess={(results) => {
                                        setMessage(`Successfully removed directory ${directory.folderPath}`);
                                        refresh();
                                      }}
                                    />
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </Collapse>
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};
